"use client";

import { Avatar, AvatarFallback, AvatarImage } from "ui/components/avatar";
import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  Blocks,
  CreditCard,
  LogOut,
  Lock,
  LockOpen,
  Sun,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "ui/components/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "ui/components/sidebar";
import { Switch } from "ui/components/switch";
import { RenderAvatar } from "components/render-avatar";
import { clearCache } from "utils/react-query";
import { formatName } from "utils/";
import { logoutUser } from "store/src/thunks/auth-thunks";
import packageJson from "../../../package.json";
import { selectUser } from "store";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { usePrivacy } from "contexts/privacy";
import { PermissionControlledInput } from "permissions/components/input";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "contexts/permission";
import { Icon } from "components/index";
import { AMOUNTLOCK, AMOUNTUNLOCK, DEDICATED, LOGOUT } from "icons/src";

export function NavUser() {
  const { isMobile } = useSidebar();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const permissions = usePermissions();
  const navigate = useNavigate();

  const { privacyOn, togglePrivacy } = usePrivacy();

  const canReadAmount = permissions.hasFieldAccess(
    "billables",
    "read",
    "amount",
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <RenderAvatar user={user} alt={user.first_name} />
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {formatName(user)}
                </span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <RenderAvatar user={user} alt={user.first_name} />
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {formatName(user)}
                  </span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                navigate("/profile/about");
              }}
            >
              <Icon icon={DEDICATED} className="size-4 mr-2" />
              <span className="flex items-center justify-between">
                Profile
                {(!user.discord_user_id || !user.github_username) && (
                  <span className="ml-2 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                )}
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                dispatch(logoutUser());
                clearCache();
              }}
            >
              <Icon icon={LOGOUT} className="size-4 mr-2" />
              Log out
            </DropdownMenuItem>
            {canReadAmount && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <div className="flex items-center justify-between w-full">
                    <span className="flex gap-2 items-center">
                      {privacyOn ? (
                        <Icon icon={AMOUNTLOCK} className="size-4 mr-2" />
                      ) : (
                        <Icon icon={AMOUNTUNLOCK} className="size-4 mr-2" />
                      )}
                      Privacy
                    </span>
                    <Switch
                      checked={privacyOn}
                      onCheckedChange={togglePrivacy}
                    />
                  </div>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <div className="text-xs text-muted-foreground text-center mt-2">
              v {packageJson.version}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
